import { Capacitor } from '@capacitor/core';
import {
	AccessControl,
	BiometryType,
	NativeBiometric,
} from '@capgo/capacitor-native-biometric';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
	faEye,
	faFaceViewfinder,
	faFingerprint,
} from '@fortawesome/pro-solid-svg-icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

// Re-exported so consumers can switch on the biometry type without a direct
// plugin dependency.
export { BiometryType };

/**
 * Biometric (Face ID / Touch ID / fingerprint) login support.
 *
 * Behavior on native iOS/Android Capacitor shells:
 *   1. After a successful credential login, call `enable({ email, refreshToken })`
 *      to persist the user's refresh token in OS-gated secure storage. The
 *      token is written behind a `SecAccessControl(.biometryCurrentSet)`
 *      keychain item (iOS) / a biometric-bound Keystore key (Android), so the
 *      operating system — not our JS — enforces that a biometric match is
 *      required to read it back.
 *   2. On subsequent visits, call `tryAutoLogin()` when the user taps
 *      "Sign in with Face ID". Reading the token via `getSecureData()` makes
 *      the OS present the biometric prompt; on a successful match it returns
 *      the stored refresh token, which the storefront exchanges at the OAuth
 *      refresh endpoint to skip the email/password screen entirely.
 *   3. `disable()` clears the stored token — wire it into Logout and into the
 *      refresh-token-rejected fallback path.
 *
 * On web (non-native): every method resolves to a "not available" result and
 * `tryAutoLogin()` returns `null` immediately, so components can render the
 * regular login form unconditionally and the biometric fast path never fires.
 *
 * Why one plugin (`@capgo/capacitor-native-biometric`):
 *   The security guarantee lives in the storage layer. The token item is
 *   created with `AccessControl.BIOMETRY_CURRENT_SET`, so the OS gates the
 *   READ on a live biometric match and auto-invalidates the item if the
 *   device's enrolled biometrics change. This is stronger than prompting for
 *   a biometric and then reading a plainly-accessible keychain value, where
 *   the token is retrievable without the biometric check ever happening.
 *
 * Keychain isolation: each tenant-branded storefront app ships with its own
 * bundle id, so its keychain is already sandboxed from every other app. The
 * keys below can therefore be stable constants without an app-id namespace.
 */

// Gated behind a biometric match (OS-enforced). Holds the OAuth refresh token.
const KEY_REFRESH_TOKEN = 'portal.biometric.refreshToken';
// Un-gated: the account email, used only to label the quick-sign-in button.
// Not a secret, and we want to display it without forcing a biometric prompt.
const KEY_EMAIL = 'portal.biometric.email';

/**
 * Silently sync the stored refresh token after a rotation.
 *
 * The OAuth refresh endpoint rotates the refresh token on every exchange
 * (a ~5-minute cadence while the app is open), so a token captured once at
 * enable-time is stale within minutes — biometric sign-in would then unlock
 * the keychain only to exchange a dead token (PD-14601). Token services call
 * this after every successful rotation; it writes only when the user has
 * biometric login enabled, and never prompts: writing a
 * `BIOMETRY_CURRENT_SET` keychain item is an un-gated `SecItemAdd` (the
 * biometric gate protects reads, not writes).
 */
export async function updateStoredBiometricSession(
	refreshToken: string,
): Promise<void> {
	if (!Capacitor.isNativePlatform()) return;
	try {
		// Enrolled-check via the UN-gated email key (PD-14700): the two keys
		// are written/deleted together by enable()/disable(), so the email's
		// presence is a faithful signal — and unlike an existence query
		// against the biometry-gated token item, it can never trip the OS
		// authentication UI. Every keychain touch here (attribute check +
		// SecItemAdd/Delete write) is auth-free; the ONLY gated operation in
		// this module stays tryAutoLogin's getSecureData read.
		const { isSaved } = await NativeBiometric.isDataSaved({
			key: KEY_EMAIL,
		});
		if (!isSaved) return;
		await NativeBiometric.setData({
			key: KEY_REFRESH_TOKEN,
			value: refreshToken,
			accessControl: AccessControl.BIOMETRY_CURRENT_SET,
		});
	} catch (e) {
		// No stored session yet, or a transient keychain error — nothing to
		// sync; the next explicit enable() rewrites everything.
		console.debug('[biometric] token sync skipped', e);
	}
}

/**
 * Existence check for a stored biometric session — attributes only, never
 * reads the gated value, so it never triggers a biometric prompt. Exposed
 * as a standalone async helper (not just hook state) for pre-render
 * decisions like the CRM app's pre-auth gate, where "is there a stored
 * session?" must be answered before choosing which screen to show.
 */
export async function hasStoredBiometricSession(): Promise<boolean> {
	if (!Capacitor.isNativePlatform()) {
		return false;
	}
	try {
		// Existence via the UN-gated email key (PD-14700) — enable()/disable()
		// write/delete both keys together, and probing the biometry-gated
		// token item (even attributes-only) risked tripping the OS auth UI on
		// device, which was one source of the doubled Face ID confirmation.
		const { isSaved } = await NativeBiometric.isDataSaved({
			key: KEY_EMAIL,
		});
		return isSaved;
	} catch {
		return false;
	}
}

/**
 * The account email stored alongside the biometric session. Deliberately
 * un-gated (it is not a secret) so sign-in screens can label the quick
 * sign-in button without forcing a biometric prompt.
 */
export async function getStoredBiometricEmail(): Promise<string | null> {
	if (!Capacitor.isNativePlatform()) {
		return null;
	}
	try {
		const { value } = await NativeBiometric.getData({ key: KEY_EMAIL });
		return value !== '' ? value : null;
	} catch {
		return null;
	}
}

export interface BiometricSession {
	email: string;
	refreshToken: string;
}

export interface BiometricCapability {
	/** True only when running inside a native Capacitor shell. */
	isNative: boolean;
	/** True when the device supports biometrics AND has at least one enrolled. */
	isAvailable: boolean;
	/** Friendly identifier for the biometric type ("Face ID", "Touch ID", etc.). */
	biometryName: string;
	biometryType: BiometryType;
}

/**
 * Detects platform + biometric availability without prompting the user.
 * Safe to call on every render; the underlying plugin call is idempotent.
 */
export function useBiometricCapability(): BiometricCapability {
	const [capability, setCapability] = useState<BiometricCapability>(() => ({
		isNative: Capacitor.isNativePlatform(),
		isAvailable: false,
		biometryName: 'Biometric',
		biometryType: BiometryType.NONE,
	}));

	useEffect(() => {
		if (!Capacitor.isNativePlatform()) return;

		void (async () => {
			try {
				// useFallback: false — only a real enrolled biometric counts;
				// a device passcode alone must not enable the feature, because
				// our stored token is gated on biometrics specifically.
				const info = await NativeBiometric.isAvailable({ useFallback: false });
				setCapability({
					isNative: true,
					isAvailable: info.isAvailable,
					biometryName: friendlyName(info.biometryType),
					biometryType: info.biometryType,
				});
			} catch (e) {
				// Plugin not registered or device error — treat as unavailable.
				console.debug('[biometric] isAvailable failed', e);
			}
		})();
	}, []);

	return capability;
}

interface BiometricLoginActions {
	/**
	 * Persist the user's session for biometric unlock. Call this AFTER a
	 * successful credential login. Prompts once to confirm the user's
	 * biometric (consent + hardware validation), then writes the refresh
	 * token behind OS-gated secure storage. Resolves true on success, false
	 * if the user declined the prompt or the device has no enrolled biometric.
	 */
	enable: (session: BiometricSession) => Promise<boolean>;

	/**
	 * Drop the stored session. Call from Logout AND when the OAuth refresh
	 * endpoint rejects the stored token (revoked, expired beyond limits,
	 * etc.) so the user falls back to credential login cleanly.
	 */
	disable: () => Promise<void>;

	/**
	 * Reads the stored refresh token, which makes the OS present the biometric
	 * prompt. On a successful match it returns the persisted session. Returns
	 * null when biometrics aren't enabled, the device doesn't support them, or
	 * the user cancels the prompt. Errors are swallowed so callers can fall
	 * through to the regular login screen.
	 */
	tryAutoLogin: (reason?: string) => Promise<BiometricSession | null>;

	/** True when a biometric session has been persisted on this device. */
	hasStoredSession: boolean;
}

export function useBiometricLogin(): BiometricLoginActions &
	BiometricCapability {
	const capability = useBiometricCapability();
	const [hasStoredSession, setHasStoredSession] = useState(false);

	useEffect(() => {
		if (!capability.isNative) return;
		void (async () => {
			try {
				// Existence via the UN-gated email key (PD-14700) — see
				// hasStoredBiometricSession for why the gated token item is
				// never probed.
				const { isSaved } = await NativeBiometric.isDataSaved({
					key: KEY_EMAIL,
				});
				setHasStoredSession(isSaved);
			} catch {
				setHasStoredSession(false);
			}
		})();
	}, [capability.isNative]);

	const enable = useCallback(
		async ({ email, refreshToken }: BiometricSession): Promise<boolean> => {
			if (!capability.isAvailable) return false;
			try {
				// Consent + hardware validation. Rejects if the user cancels or
				// no biometric matches, in which case we leave nothing stored.
				await NativeBiometric.verifyIdentity({
					reason: 'Enable quick sign-in with biometrics',
					useFallback: false,
				});
				// Token: gated behind a biometric match, auto-invalidated on
				// enrollment change.
				await NativeBiometric.setData({
					key: KEY_REFRESH_TOKEN,
					value: refreshToken,
					accessControl: AccessControl.BIOMETRY_CURRENT_SET,
				});
				// Email: un-gated, so the quick-sign-in button can show the
				// account without a prompt.
				await NativeBiometric.setData({ key: KEY_EMAIL, value: email });
				setHasStoredSession(true);
				return true;
			} catch (e) {
				console.debug('[biometric] enable rejected', e);
				return false;
			}
		},
		[capability.isAvailable],
	);

	const disable = useCallback(async (): Promise<void> => {
		if (!capability.isNative) return;
		try {
			await NativeBiometric.deleteData({ key: KEY_REFRESH_TOKEN });
			await NativeBiometric.deleteData({ key: KEY_EMAIL });
		} catch (e) {
			// Plugin error or keys missing — either way, we're "disabled".
			console.debug('[biometric] disable cleanup partial', e);
		}
		setHasStoredSession(false);
	}, [capability.isNative]);

	const tryAutoLogin = useCallback(
		async (
			reason = 'Sign in to your account',
		): Promise<BiometricSession | null> => {
			if (!capability.isNative || !capability.isAvailable) return null;

			try {
				// Go straight to the gated read — no existence precheck against
				// the protected item (PD-14700: probing it, even attributes-only,
				// risked an extra OS auth evaluation on device, doubling the
				// Face ID confirmation). A missing item rejects with the
				// plugin's not-found error and falls through to null exactly
				// like the old precheck did.
				//
				// The gated read presents the OS biometric prompt. A cancel or
				// failed match rejects here and we fall through to null.
				const { value: refreshToken } = await NativeBiometric.getSecureData({
					key: KEY_REFRESH_TOKEN,
					reason,
				});
				if (refreshToken === '') {
					await disable();
					return null;
				}

				// Email is best-effort labeling; never block sign-in on it.
				let email = '';
				try {
					const stored = await NativeBiometric.getData({ key: KEY_EMAIL });
					email = stored.value;
				} catch {
					// No stored email — proceed with the token alone.
				}
				return { email, refreshToken };
			} catch (e) {
				// User cancelled, biometry locked out, item invalidated, or a
				// plugin error — let the caller fall through to credential login.
				console.debug('[biometric] auto-login cancelled or failed', e);
				return null;
			}
		},
		[capability.isAvailable, capability.isNative, disable],
	);

	// Memoized: consumers put this object in effect dependency arrays, and a
	// fresh identity per render made those effects refire on every state
	// change — the cause of the repeated Face ID prompt loop during enrollment
	// (PD-14601).
	return useMemo(
		() => ({
			...capability,
			hasStoredSession,
			enable,
			disable,
			tryAutoLogin,
		}),
		[capability, hasStoredSession, enable, disable, tryAutoLogin],
	);
}

function friendlyName(type: BiometryType): string {
	switch (type) {
		case BiometryType.FACE_ID:
			return 'Face ID';
		case BiometryType.TOUCH_ID:
			return 'Touch ID';
		case BiometryType.FINGERPRINT:
			return 'Fingerprint';
		case BiometryType.FACE_AUTHENTICATION:
			return 'Face Authentication';
		case BiometryType.IRIS_AUTHENTICATION:
			return 'Iris';
		default:
			return 'Biometric';
	}
}

/**
 * Icon matching the device's biometry type. A fingerprint glyph next to a
 * "Sign in with Face ID" label reads wrong (PD-14659 review feedback) — face
 * authentication gets the face-scan glyph; the fingerprint stays only for
 * fingerprint-style sensors, and doubles as the generic-biometric fallback.
 */
export function biometricIconFor(type: BiometryType): IconDefinition {
	switch (type) {
		case BiometryType.FACE_ID:
		case BiometryType.FACE_AUTHENTICATION:
			return faFaceViewfinder;
		case BiometryType.IRIS_AUTHENTICATION:
			return faEye;
		default:
			return faFingerprint;
	}
}
